import React, { useEffect, useState } from 'react';
import { ChevronRightIcon } from '../components/icons';
import { JourneyMap, LessonPlayer, StepComplete } from '../components/modals/JourneyModal';
import { FullScreenModal } from '../components/FullScreenModal';
import { Condition } from '../data/conditions';
import { TINTS } from '../data/mock';
type JourneyView = 'list' | 'map' | 'lesson' | 'complete';
/**
 * Journey — full-screen, in-navigation flow (no bottom modal).
 *
 *   list (conditions) → map (the journey) → lesson (the step) → complete
 *
 * A single SlideOver hosts the whole flow; its content swaps by `view`. The ×
 * always tears the flow down and returns to the dashboard; the ← pops one
 * step. This SlideOver + "close everything" pattern is reusable elsewhere.
 */
export function JourneyScreen({
  open,
  onClose,
  conditions
}: {
  open: boolean;
  onClose: () => void;
  conditions: Condition[];
}) {
  const multiple = conditions.length > 1;
  const root: JourneyView = multiple ? 'list' : 'map';
  const [view, setView] = useState<JourneyView>(root);
  const [active, setActive] = useState<Condition | null>(conditions[0] ?? null);
  // Re-seed the entry view each time the flow opens
  useEffect(() => {
    if (open) {
      setView(multiple ? 'list' : 'map');
      setActive(conditions[0] ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const goBack = () => {
    if (view === 'lesson' || view === 'complete') setView('map');else if (view === 'map') multiple ? setView('list') : onClose();else onClose();
  };
  const canGoBack = view !== root;
  return <FullScreenModal open={open} onBack={canGoBack ? goBack : undefined} onClose={onClose} direction="up" eyebrow={view === 'list' ? 'Your journeys' : undefined} title={view === 'list' ? 'Journey' : undefined} padded={false} resetOnOpen={false} data-id="element-962">
      {view === 'list' && <div className="px-5 pt-3" data-id="element-963">
          <p className="mb-4 text-[13px] leading-snug text-ink-soft" data-id="element-964">
            {multiple ? `You're tracking ${conditions.length} conditions. Pick a journey to continue.` : 'Continue your programme.'}
          </p>
          <div className="space-y-2.5" data-id="element-965">
            {conditions.map(c => {
          const tint = TINTS[c.tint];
          const Icon = c.icon;
          return <button key={c.id} onClick={() => {
            setActive(c);
            setView('map');
          }} className="flex w-full items-center gap-3.5 rounded-card border border-line bg-cream px-4 py-4 text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98]" data-id="element-966">
                  <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tint.soft} ${tint.text}`} data-id="element-967">
                    <Icon size={22} data-id="element-968" />
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-969">
                    <h3 className="font-display text-[16px] font-semibold leading-snug text-ink" data-id="element-970">
                      {c.label}
                    </h3>
                    <p className="mt-0.5 truncate text-[13px] leading-snug text-ink-soft" data-id="element-971">
                      {c.programme}
                    </p>
                  </div>
                  <ChevronRightIcon size={18} strokeWidth={2.4} className="shrink-0 text-ink-faint" data-id="element-972" />
                </button>;
        })}
          </div>
        </div>}

      {view === 'map' && <div className="dhp-fade" data-id="element-973">
          <JourneyMap onResume={() => setView('lesson')} programme={active?.programme} data-id="element-974" />
        </div>}

      {view === 'lesson' && <div className="dhp-fade" data-id="element-975">
          <LessonPlayer onExit={() => setView('map')} onComplete={() => setView('complete')} data-id="element-976" />
        </div>}

      {view === 'complete' && <div className="dhp-fade" data-id="element-977">
          <StepComplete onDone={onClose} data-id="element-978" />
        </div>}
    </FullScreenModal>;
}